/* ZSportsTech — netsessions-api.js
   Shared client for the Apps Script booking backend + a small modal shell.
   Loaded by snippets/netsessions-config.liquid, ahead of the section scripts.
   Exposes window.SCNS. Vanilla JS, no dependencies.
   ─────────────────────────────────────────────────── */
(function () {
  'use strict';

  /* Two sections on the same page each render the config snippet, so this file
     can be included twice. Run once. */
  if (window.SCNS) return;

  var CFG = window.SC_NS_CONFIG || {};

  /* Actions that act on a customer's own data. The backend rejects these
     without a valid signed identity, and ignores any `email` in the body. */
  var GATED = ['balance', 'sync', 'book', 'myBookings', 'cancel'];

  /* ── Transport ────────────────────────────────────────────────────────
     Content-Type MUST stay text/plain: that keeps this a CORS "simple
     request". Apps Script web apps do not answer the OPTIONS preflight that
     application/json would trigger, so the browser would block the call
     before it left. doPost() reads e.postData.contents and JSON.parses it
     itself, so a text/plain body carrying JSON is exactly what it wants.
     The /exec URL 302s to script.googleusercontent.com; fetch follows that
     redirect, and only the final response carries Access-Control-Allow-Origin. */
  function post(payload) {
    if (!CFG.endpoint) return Promise.reject(new Error('ns_endpoint_missing'));
    return fetch(CFG.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload),
      redirect: 'follow'
    }).then(function (r) {
      return r.text();
    }).then(function (text) {
      var data;
      try { data = JSON.parse(text); }
      catch (e) { throw new Error('bad_response'); }
      return data;
    });
  }

  /* Resolve to the payload on ok, reject with the backend's error code. */
  function call(payload) {
    if (GATED.indexOf(payload.action) !== -1) {
      if (!CFG.identity) return Promise.reject(new Error('unauthorized'));
      payload.identity = CFG.identity;
    }
    return post(payload).then(function (res) {
      if (!res || res.ok !== true) throw new Error((res && res.error) || 'server_error');
      return res;
    });
  }

  var MESSAGES = {
    ns_endpoint_missing: 'Booking is not set up yet. Please use the enquiry form below.',
    bad_response:        'The booking service returned something unexpected. Please try again.',
    server_error:        'Something went wrong at our end. Please try again.',
    unauthorized:        'Please sign in again; your session could not be verified.',
    identity_expired:    'Your session has expired. Reload the page and try again.',
    auth_not_configured: 'Booking is not fully configured yet. Please contact us.',
    no_balance:          'You have no sessions of that kind left. Buy a package first.',
    slot_taken:          'Sorry, that slot was just taken. Please pick another.',
    slot_not_found:      'That time is not available on this date.',
    /* Cancellation (24-hour window). */
    too_late:            'This booking is within 24 hours, so it can no longer be cancelled online. Please call us.',
    already_started:     'This session has already started and can’t be cancelled.',
    not_cancellable:     'This booking can’t be cancelled; it may already be cancelled or completed.',
    booking_not_found:   'We couldn’t find that booking on your account.',
    missing_booking:     'Something went wrong; please reload and try again.',
    /* Off-hours (outside 11 AM–7 PM) requests, which the owner confirms. */
    offhours_clinic:     'Clinic sessions run between 11 AM and 7 PM. Please call us to arrange another time.',
    too_soon_to_request: 'That slot is too close to now for us to confirm it in time. Please call us, or pick a time at least 10 minutes away.',
    request_expired:     'That request has expired and your session credit has been returned. Please pick a slot again.',
    /* Availability is read from Google Calendar. If we cannot reach it we refuse
       to guess, because guessing means double-booking a net. */
    calendar_unavailable: 'We can’t check the schedule right now. Please try again shortly.',
    /* Slot-first clinic holds. */
    holds_disabled:      'Online clinic booking is paused. Please contact us to book.',
    hold_limit_reached:  'You’ve held several slots today already. Please complete a booking, or try again tomorrow.',
    /* Same wording as hold_limit_reached — from a shopper's point of view it
       is the same situation, just counted by phone number instead of email. */
    phone_hold_limit:    'You’ve held several slots today already. Please complete a booking, or try again tomorrow.',
    /* The fillMs anti-bot check (see Code.gs CFG.MIN_FILL_MS): the form was
       submitted faster than a human could plausibly have typed it. */
    too_fast:            'Please take a moment to check your details, then press Continue again.',
    busy_try_again:      'Someone else is booking right now. Try again in a moment.',
    /* verifyPageToken_ refused the hold: the Liquid-minted token has expired
       or never matched. Also used for the honeypot's 'unauthorized' on the
       clinic page — see section-clinic-book.js. */
    page_expired:        'This page has been open too long. Please reload the page and try again.',
    bad_date:            'Please choose a valid date.',
    bad_email:           'Please enter a valid email address.',
    missing_name:        'Please enter your name.',
    missing_phone:       'Please enter your phone number.',
    bad_phone:            'Please enter a valid 10-digit mobile number (or include your country code, e.g. +44…).',
    missing_date:        'Please choose a date.',
    missing_start:       'Please choose a time slot.',
    missing_message:     'Please tell us what you need.',
    store_failed:        'We could not save that. Please try again.',
    cart_failed:         'We could not start checkout. Please try again.',
    network_error:       'Network problem: check your connection and try again.'
  };
  function message(code) {
    if (code === 'Failed to fetch' || code === 'NetworkError when attempting to fetch resource.') code = 'network_error';
    return MESSAGES[code] || MESSAGES.server_error;
  }
  function errText(err) { return message(err && err.message ? err.message : String(err)); }

  /* ── Modal shell ──────────────────────────────────────────────────────
     Styles are injected once and namespaced sc-nsm__. They reuse the theme's
     existing custom properties, so the dialog matches the navy/gold sections
     without touching any existing component's CSS. */
  var stylesInjected = false;
  function injectStyles() {
    if (stylesInjected) return;
    stylesInjected = true;
    var css = [
      /* While a payment sheet is up, the booking sheet stands down entirely —
         see the comment at hideBooking() in section-clinic-book.js. */
      '.sc-nsm--paying{display:none!important;}',
      /* Belt and braces for anything else that might stack: the payment sheet
         must always be reachable, whatever else is on screen. */
      '.razorpay-container{z-index:2147483647!important;}',
      /* The escape hatch from a wedged payment sheet. Same z-index as
         Razorpay's container and appended after it, so it paints on top;
         parked at the bottom where it cannot cover the payment options. */
      '.sc-nsm__payesc{position:fixed;z-index:2147483647;left:50%;transform:translateX(-50%);' +
        'bottom:calc(12px + env(safe-area-inset-bottom,0px));font-family:var(--font-sans);font-size:0.82rem;' +
        'font-weight:600;color:#fff;background:rgba(10,13,43,0.92);border:1px solid rgba(255,255,255,0.28);' +
        'border-radius:9999px;padding:0.6rem 1.1rem;box-shadow:0 6px 20px rgba(0,0,0,0.35);cursor:pointer;}',
      '.sc-nsm{position:fixed;inset:0;z-index:200;display:flex;align-items:center;justify-content:center;padding:1rem;background:rgba(4,10,18,0.72);backdrop-filter:blur(3px);}',
      '.sc-nsm[hidden]{display:none;}',
      /* NOTE: CSS math functions need spaces around +/- — "1rem+1.5vw" is
         invalid and the browser drops the whole declaration. */
      /* No top padding on the panel — the sticky head carries it, so it can
         sit flush against the scrollport top (a negative top margin would
         offset the sticky position by the same amount and leave a gap). */
      '.sc-nsm__panel{--nsm-pad:clamp(1.25rem, 1rem + 1.5vw, 2rem);width:100%;max-width:32rem;max-height:90vh;overflow-y:auto;background:var(--dark-raised);border:1px solid var(--dark-hairline);border-radius:var(--r-card);padding:0 var(--nsm-pad) var(--nsm-pad);color:var(--dark-text-primary);scrollbar-width:thin;scrollbar-color:var(--dark-hairline) transparent;}',
      '.sc-nsm__panel::-webkit-scrollbar{width:7px;}',
      '.sc-nsm__panel::-webkit-scrollbar-track{background:transparent;}',
      '.sc-nsm__panel::-webkit-scrollbar-thumb{background:var(--dark-hairline);border-radius:4px;}',
      /* Header stays pinned while the body scrolls; negative margins let it
         span the full panel width so content slides beneath it cleanly. */
      '.sc-nsm__head{position:sticky;top:0;z-index:3;display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;background:var(--dark-raised);margin:0 calc(-1 * var(--nsm-pad)) 1.1rem;padding:var(--nsm-pad) var(--nsm-pad) 0.85rem;border-bottom:1px solid var(--dark-hairline);}',
      '.sc-nsm__title{font-family:var(--font-sans);font-weight:500;font-size:clamp(1.1rem, 1rem + 0.6vw, 1.4rem);line-height:1.2;}',
      '.sc-nsm__sub{font-family:var(--font-mono);font-size:0.68rem;letter-spacing:0.12em;text-transform:uppercase;color:var(--dark-text-label);margin-top:0.4rem;}',
      '.sc-nsm__close{width:2rem;height:2rem;flex-shrink:0;display:flex;align-items:center;justify-content:center;border-radius:50%;color:var(--dark-text-label);transition:color var(--t-fast) var(--ease-out),background var(--t-fast) var(--ease-out);}',
      '.sc-nsm__close:hover{color:var(--dark-text-primary);background:rgba(255,255,255,0.08);}',
      '.sc-nsm__label{display:block;font-family:var(--font-mono);font-size:0.66rem;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:var(--dark-text-label);margin-bottom:0.5rem;}',
      '.sc-nsm__field{margin-bottom:1.1rem;}',
      '.sc-nsm__input{width:100%;font-family:var(--font-sans);font-size:1rem;color:var(--dark-text-primary);background:transparent;border:none;border-bottom:1px solid var(--dark-hairline);padding:0.55rem 0;transition:border-color var(--t-fast) var(--ease-out);}',
      '.sc-nsm__input::placeholder{color:var(--dark-text-label);}',
      '.sc-nsm__input:focus{outline:none;border-bottom-color:var(--gold);}',
      '.sc-nsm__input::-webkit-calendar-picker-indicator{filter:invert(1);opacity:0.5;cursor:pointer;}',
      '.sc-nsm__choices{display:flex;flex-wrap:wrap;gap:0.6rem;}',
      '.sc-nsm__choice{font-family:var(--font-sans);font-size:0.92rem;font-weight:500;color:var(--dark-text-secondary);border:1px solid var(--dark-hairline);border-radius:var(--r-chip);padding:0.6rem 1.2rem;transition:color var(--t-fast) var(--ease-out),border-color var(--t-fast) var(--ease-out),background var(--t-fast) var(--ease-out);}',
      '.sc-nsm__choice:hover:not(:disabled){color:var(--dark-text-primary);border-color:var(--dark-accent-border);}',
      '.sc-nsm__choice[aria-pressed="true"]{color:var(--gold);border-color:var(--dark-accent-border);background:rgba(212,175,55,0.08);}',
      '.sc-nsm__slots{display:grid;grid-template-columns:repeat(auto-fill,minmax(6.2rem,1fr));gap:0.5rem;}',
      '.sc-nsm__slot{font-family:var(--font-mono);font-size:0.82rem;color:var(--dark-text-secondary);border:1px solid var(--dark-hairline);border-radius:var(--r-chip);padding:0.6rem 0.4rem;text-align:center;transition:color var(--t-fast) var(--ease-out),border-color var(--t-fast) var(--ease-out),background var(--t-fast) var(--ease-out);}',
      '.sc-nsm__slot:hover:not(:disabled){color:var(--dark-text-primary);border-color:var(--dark-accent-border);}',
      '.sc-nsm__slot[aria-pressed="true"]{color:var(--gold);border-color:var(--dark-accent-border);background:rgba(212,175,55,0.08);}',
      /* Taken/past/unbookable slots are hidden outright — a wall of
         struck-through chips reads as clutter. */
      '.sc-nsm__slot:disabled{display:none;}',
      /* Off-hours slots (outside 11 AM–7 PM): bookable, but a dashed outline
         marks them as outside standard hours. */
      '.sc-nsm__slot--offhours{border-style:dashed;}',
      /* Sticky so the CTA is always reachable while the body scrolls. Solid
         backgrounds (see :disabled below) keep scrolling content from
         bleeding through; the upward shadow separates it from that content.
         NB: don't try a z-index:-1 ::after backdrop here — the sticky button
         is its own stacking context, so the pseudo paints over its bg. */
      '.sc-nsm__submit{position:sticky;bottom:0;z-index:2;width:100%;display:inline-flex;align-items:center;justify-content:center;gap:0.5rem;font-family:var(--font-sans);font-size:1.02rem;font-weight:600;color:var(--navy);background:var(--gold);border-radius:var(--r-button);padding:0.95rem;margin-top:0.5rem;box-shadow:0 -14px 22px -10px rgba(4,10,18,0.6);transition:background var(--t-fast) var(--ease-out),opacity var(--t-fast) var(--ease-out);}',
      '.sc-nsm__submit:hover:not(:disabled){background:var(--dark-accent-hover);}',
      /* Solid (not translucent) disabled state — the sticky CTA sits over
         scrolling content, so opacity would let it bleed through. */
      '.sc-nsm__submit:disabled{background:#4D4536;color:rgba(244,243,237,0.60);cursor:not-allowed;}',
      '.sc-nsm__msg{font-family:var(--font-sans);font-size:0.9rem;line-height:1.45;border-radius:var(--r-chip);padding:0.75rem 0.9rem;margin-bottom:1rem;}',
      '.sc-nsm__msg[hidden]{display:none;}',
      '.sc-nsm__msg--error{color:#FFB4A8;background:rgba(255,90,70,0.10);border:1px solid rgba(255,90,70,0.28);}',
      '.sc-nsm__msg--info{color:var(--dark-text-secondary);background:rgba(255,255,255,0.04);border:1px solid var(--dark-hairline);}',
      /* The off-hours disclaimer: this slot needs the owner to confirm it. */
      '.sc-nsm__msg--warn{color:#F0D68A;background:rgba(212,175,55,0.09);border:1px solid rgba(212,175,55,0.32);}',
      '.sc-nsm__note{font-family:var(--font-mono);font-size:0.64rem;letter-spacing:0.1em;text-transform:uppercase;color:var(--dark-text-label);margin-top:1rem;text-align:center;}',

      /* ── Booking calendar: month grid beside a slot column ── */
      '.sc-nsm__panel--wide{max-width:46rem;}',
      /* stretch: both panes share the calendar's height, so the slot pane's
         empty-state message can centre instead of floating at the top. */
      '.sc-nsm__grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:0.85rem;align-items:stretch;margin-bottom:1.1rem;}',
      '@media (max-width:720px){.sc-nsm__grid{grid-template-columns:1fr;}}',
      '.sc-nsm__pane{display:flex;flex-direction:column;min-height:0;border:1px solid var(--dark-hairline);border-radius:var(--r-card);padding:0.85rem;background:rgba(255,255,255,0.02);}',
      '.sc-nsm__calhead{display:flex;align-items:center;justify-content:space-between;gap:0.5rem;margin-bottom:0.7rem;}',
      '.sc-nsm__calmon{font-family:var(--font-sans);font-weight:500;font-size:0.98rem;color:var(--dark-text-primary);}',
      '.sc-nsm__navbtn{width:1.9rem;height:1.9rem;flex-shrink:0;display:flex;align-items:center;justify-content:center;border-radius:50%;border:1px solid var(--dark-hairline);color:var(--dark-text-secondary);transition:color var(--t-fast) var(--ease-out),border-color var(--t-fast) var(--ease-out);}',
      '.sc-nsm__navbtn:hover:not(:disabled){color:var(--gold);border-color:var(--dark-accent-border);}',
      '.sc-nsm__navbtn:disabled{opacity:0.28;cursor:not-allowed;}',
      '.sc-nsm__dows{display:grid;grid-template-columns:repeat(7,1fr);gap:3px;margin-bottom:4px;}',
      '.sc-nsm__dow{text-align:center;font-family:var(--font-mono);font-size:0.58rem;letter-spacing:0.06em;color:var(--dark-text-label);padding:0.2rem 0;}',
      '.sc-nsm__days{display:grid;grid-template-columns:repeat(7,1fr);gap:3px;}',
      '.sc-nsm__day{aspect-ratio:1;display:flex;align-items:center;justify-content:center;font-family:var(--font-mono);font-size:0.8rem;color:var(--dark-text-secondary);background:transparent;border:1px solid transparent;border-radius:8px;transition:color var(--t-fast) var(--ease-out),border-color var(--t-fast) var(--ease-out),background var(--t-fast) var(--ease-out);}',
      '.sc-nsm__day:hover:not(:disabled){color:var(--dark-text-primary);border-color:var(--dark-accent-border);}',
      '.sc-nsm__day--today:not([aria-selected="true"]){border-color:var(--dark-accent-border);color:var(--gold);}',
      '.sc-nsm__day[aria-selected="true"]{background:var(--gold);color:var(--navy);font-weight:700;border-color:var(--gold);}',
      '.sc-nsm__day:disabled{opacity:0.2;cursor:not-allowed;}',
      '.sc-nsm__day--blank{visibility:hidden;pointer-events:none;}',
      '.sc-nsm__callegend{margin-top:0.6rem;font-family:var(--font-mono);font-size:0.58rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--dark-text-label);}',
      '.sc-nsm__panehead{display:flex;align-items:baseline;justify-content:space-between;gap:0.5rem;margin-bottom:0.6rem;min-height:1.2rem;}',
      '.sc-nsm__panetitle{font-family:var(--font-mono);font-size:0.66rem;font-weight:600;letter-spacing:0.12em;text-transform:uppercase;color:var(--gold);}',
      '.sc-nsm__panecount{font-family:var(--font-mono);font-size:0.6rem;letter-spacing:0.06em;color:var(--dark-text-label);white-space:nowrap;}',
      '.sc-nsm__slotscroll{max-height:13.5rem;overflow-y:auto;overscroll-behavior:contain;scrollbar-width:thin;scrollbar-color:var(--dark-hairline) transparent;}',
      '.sc-nsm__slotscroll::-webkit-scrollbar{width:7px;}',
      '.sc-nsm__slotscroll::-webkit-scrollbar-track{background:transparent;}',
      '.sc-nsm__slotscroll::-webkit-scrollbar-thumb{background:var(--dark-hairline);border-radius:4px;}',
      /* Wide enough for a full "8:00–8:30 AM" range on one line. */
      '.sc-nsm__pane .sc-nsm__slots{grid-template-columns:repeat(auto-fill,minmax(7.4rem,1fr));gap:0.4rem;}',
      '.sc-nsm__pane .sc-nsm__slot{padding:0.55rem 0.3rem;font-size:0.72rem;white-space:nowrap;}',
      '.sc-nsm__panemsg{flex:1;display:flex;align-items:center;justify-content:center;font-family:var(--font-sans);font-size:0.85rem;line-height:1.5;color:var(--dark-text-label);padding:1.5rem 0.5rem;text-align:center;}',
      /* Clinic offer banner. Deliberately NOT a sc-nsm__msg: an info panel reads
         as fine print, and this is the first thing the shopper should see. Gold
         on a gold wash rather than a red "sale" badge — the rest of the store is
         gold-on-navy, and a discount sticker in someone else's colours reads as
         a third-party widget rather than a price the shop is standing behind. */
      '.sc-nsm__offer{margin-bottom:1rem;padding:0.9rem 1rem;border-radius:14px;' +
        'border:1px solid rgba(212,175,55,0.38);' +
        'background:linear-gradient(135deg,rgba(212,175,55,0.18),rgba(212,175,55,0.04));}',
      '.sc-nsm__offer-head{display:flex;align-items:center;justify-content:space-between;gap:0.75rem;margin-bottom:0.4rem;}',
      '.sc-nsm__offer-tag{font-family:var(--font-mono);font-size:0.64rem;font-weight:600;letter-spacing:0.14em;text-transform:uppercase;color:var(--gold);}',
      '.sc-nsm__offer-save{font-family:var(--font-mono);font-size:0.6rem;font-weight:700;letter-spacing:0.06em;' +
        'color:var(--navy);background:var(--gold);border-radius:var(--r-chip);padding:0.2rem 0.55rem;white-space:nowrap;}',
      '.sc-nsm__offer-prices{display:flex;align-items:baseline;gap:0.65rem;flex-wrap:wrap;}',
      '.sc-nsm__offer-was{font-family:var(--font-sans);font-size:1.05rem;color:var(--dark-text-label);text-decoration:line-through;}',
      '.sc-nsm__offer-now{font-family:var(--font-sans);font-size:2rem;font-weight:700;line-height:1;color:var(--gold);}',
      /* Clinic confirmation. Built to be screenshotted: everything that matters
         fits one phone screen above the map, and the booking id is the largest
         thing after the headline because it is what staff ask for. */
      '.sc-clconf{text-align:center;}',
      '.sc-clconf__tick{width:3rem;height:3rem;margin:0 auto 0.9rem;display:flex;align-items:center;justify-content:center;' +
        'border-radius:50%;font-size:1.4rem;color:var(--gold);border:1px solid var(--dark-accent-border);background:rgba(212,175,55,0.10);}',
      '.sc-clconf__title{font-family:var(--font-sans);font-weight:600;font-size:1.3rem;margin:0 0 0.35rem;}',
      '.sc-clconf__sub{font-family:var(--font-sans);font-size:0.9rem;color:var(--dark-text-secondary);margin:0 0 1rem;}',
      '.sc-clconf__ref{display:inline-flex;flex-direction:column;gap:0.2rem;padding:0.6rem 1.1rem;margin-bottom:0.9rem;' +
        'border-radius:12px;border:1px solid rgba(212,175,55,0.38);background:rgba(212,175,55,0.10);}',
      '.sc-clconf__reflabel{font-family:var(--font-mono);font-size:0.58rem;letter-spacing:0.14em;text-transform:uppercase;color:var(--gold);}',
      '.sc-clconf__refval{font-family:var(--font-mono);font-size:0.82rem;color:var(--dark-text-primary,#fff);word-break:break-all;}',
      '.sc-clconf__mail{font-family:var(--font-sans);font-size:0.82rem;line-height:1.55;color:var(--dark-text-label);margin:0 0 1rem;}',
      '.sc-clconf__mail strong{color:var(--dark-text-secondary);font-weight:600;}',
      '.sc-clconf__shot{font-family:var(--font-sans);font-size:0.88rem;font-weight:600;color:var(--gold);margin:0 0 1.1rem;}',
      '.sc-clconf__rows{display:grid;grid-template-columns:auto 1fr;gap:0.45rem 1rem;text-align:left;margin:0 0 1.2rem;' +
        'padding:0.9rem 1rem;border-radius:12px;border:1px solid var(--dark-hairline);background:rgba(255,255,255,0.03);}',
      '.sc-clconf__rows dt{font-family:var(--font-mono);font-size:0.6rem;letter-spacing:0.1em;text-transform:uppercase;' +
        'color:var(--dark-text-label);align-self:center;}',
      '.sc-clconf__rows dd{margin:0;font-family:var(--font-sans);font-size:0.92rem;}',
      '.sc-clconf__where{text-align:left;margin-bottom:1.1rem;}',
      '.sc-clconf__wherelabel{font-family:var(--font-mono);font-size:0.6rem;letter-spacing:0.1em;text-transform:uppercase;color:var(--dark-text-label);}',
      '.sc-clconf__addr{font-family:var(--font-sans);font-size:0.95rem;margin:0.3rem 0 0.7rem;}',
      /* aspect-ratio + max-width so the map never forces a sideways scroll. */
      '.sc-clconf__map{position:relative;width:100%;max-width:100%;aspect-ratio:16/10;border-radius:12px;overflow:hidden;border:1px solid var(--dark-hairline);}',
      '.sc-clconf__map iframe{position:absolute;inset:0;width:100%;height:100%;border:0;}',
      '.sc-clconf__dir{display:inline-block;margin-top:0.6rem;font-family:var(--font-sans);font-size:0.88rem;font-weight:600;color:var(--gold);}',
      '.sc-clconf__note{font-family:var(--font-sans);font-size:0.85rem;line-height:1.5;color:var(--dark-text-secondary);margin:0 0 0.5rem;}',
      '.sc-clconf__order{font-family:var(--font-mono);font-size:0.65rem;letter-spacing:0.06em;color:var(--dark-text-label);margin:0;}',
      '.sc-nsm__row2{display:grid;grid-template-columns:1fr 1fr;gap:1rem;}',
      '@media (max-width:560px){.sc-nsm__row2{grid-template-columns:1fr;gap:0;}}',
      '.sc-nsm__done{text-align:center;padding:1rem 0;}',
      '.sc-nsm__done-tick{width:3.2rem;height:3.2rem;margin:0 auto 1rem;display:flex;align-items:center;justify-content:center;border-radius:50%;color:var(--gold);border:1px solid var(--dark-accent-border);background:rgba(212,175,55,0.08);}',
      '.sc-nsm__done-title{font-family:var(--font-sans);font-weight:500;font-size:1.2rem;margin-bottom:0.5rem;}',
      '.sc-nsm__done-sub{font-family:var(--font-sans);font-size:0.95rem;color:var(--dark-text-secondary);line-height:1.5;}',
      '.sc-nsm__done-cta{display:inline-flex;align-items:center;gap:0.5rem;margin-top:1.4rem;font-family:var(--font-sans);font-weight:600;color:var(--navy);background:var(--gold);padding:0.8rem 1.6rem;border-radius:var(--r-button);}',
      /* ── PHONES ───────────────────────────────────────────────────────────
         Measured at 414px before writing any of this. The layout already
         stacked and did not scroll sideways, and inputs were already 16px so
         iOS does not zoom on focus. What was actually wrong was ergonomics:
         1210px of content inside an 806px panel, day cells at 41px (under the
         44px thumb target), slot chips at 0.72rem, and a floating card wasting
         a gutter on both sides of a screen that has none to spare.

         So: a bottom sheet rather than a floating card, thumb-sized controls,
         and a tighter vertical rhythm to cut the scroll. */
      '@media (max-width:560px){',
      /* Sheet, not card: full width, flush to the bottom, rounded at the top. */
      '.sc-nsm{align-items:flex-end;padding:0;}',
      '.sc-nsm__panel{--nsm-pad:1rem;max-width:100%;max-height:94vh;border-radius:18px 18px 0 0;border-bottom:0;}',
      '.sc-nsm__head{margin-bottom:0.85rem;padding-top:1rem;padding-bottom:0.7rem;}',
      '.sc-nsm__title{font-size:1.12rem;}',
      /* A 44px minimum on everything a thumb has to hit. */
      '.sc-nsm__day{min-height:44px;font-size:0.86rem;border-radius:10px;}',
      '.sc-nsm__days{gap:4px;}',
      '.sc-nsm__choice{padding:0.72rem 1.15rem;min-height:44px;}',
      '.sc-nsm__pane .sc-nsm__slot{padding:0.8rem 0.35rem;font-size:0.8rem;min-height:44px;}',
      '.sc-nsm__pane .sc-nsm__slots{grid-template-columns:repeat(auto-fill,minmax(8.2rem,1fr));gap:0.45rem;}',
      /* The slot list sits BELOW the calendar here, so it can afford real
         height — 13.5rem meant scrolling a list inside a scrolling sheet. */
      '.sc-nsm__slotscroll{max-height:min(42vh,20rem);}',
      /* Reclaimed vertical space, a little from each block rather than
         gutting any one of them. */
      '.sc-nsm__offer{padding:0.75rem 0.85rem;margin-bottom:0.8rem;}',
      '.sc-nsm__offer-now{font-size:1.75rem;}',
      '.sc-nsm__grid{gap:0.8rem;}',
      '.sc-nsm__submit{padding:1rem;font-size:1.05rem;}',
      /* Clear of the iPhone home indicator. */
      '.sc-nsm__panel{padding-bottom:calc(var(--nsm-pad) + env(safe-area-inset-bottom,0px));}',

      /* Confirmation: this is the screen being screenshotted, so it has to
         hold together in one portrait frame. */
      '.sc-clconf__title{font-size:1.18rem;}',
      '.sc-clconf__refval{font-size:0.74rem;}',
      '.sc-clconf__rows{padding:0.8rem 0.85rem;gap:0.4rem 0.8rem;}',
      '.sc-clconf__rows dd{font-size:0.88rem;}',
      /* Portrait-friendlier map: 16/10 is a letterbox on a phone. */
      '.sc-clconf__map{aspect-ratio:4/3;}',
      /* Trimmed so the part staff actually check — id, when, net, name —
         lands in the first screenful, with the map following as the thing you
         scroll to when you are travelling rather than when you are proving. */
      '.sc-clconf__tick{width:2.5rem;height:2.5rem;margin-bottom:0.7rem;font-size:1.2rem;}',
      '.sc-clconf__sub{margin-bottom:0.8rem;}',
      '.sc-clconf__shot{margin-bottom:0.9rem;font-size:0.85rem;}',
      '.sc-clconf__mail{font-size:0.79rem;margin-bottom:0.85rem;}',
      '.sc-clconf__note{font-size:0.82rem;margin-bottom:0.4rem;}',
      '}',
      '@media (prefers-reduced-motion:reduce){.sc-nsm__choice,.sc-nsm__slot,.sc-nsm__submit,.sc-nsm__close{transition:none;}}'
    ].join('');
    var tag = document.createElement('style');
    tag.textContent = css;
    document.head.appendChild(tag);
  }

  var TICK = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m4 12 5.5 5.5L20 7"/></svg>';

  function modal(opts) {
    injectStyles();
    var lastFocus = document.activeElement;

    var overlay = document.createElement('div');
    overlay.className = 'sc-nsm';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');

    var panel = document.createElement('div');
    panel.className = 'sc-nsm__panel' + (opts.wide ? ' sc-nsm__panel--wide' : '');

    var head = document.createElement('div');
    head.className = 'sc-nsm__head';
    var titleWrap = document.createElement('div');
    var title = document.createElement('h2');
    title.className = 'sc-nsm__title';
    title.textContent = opts.title || '';
    titleWrap.appendChild(title);
    if (opts.subtitle) {
      var sub = document.createElement('p');
      sub.className = 'sc-nsm__sub';
      sub.textContent = opts.subtitle;
      titleWrap.appendChild(sub);
    }
    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'sc-nsm__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg>';
    head.appendChild(titleWrap);
    head.appendChild(closeBtn);

    var body = document.createElement('div');

    panel.appendChild(head);
    panel.appendChild(body);
    overlay.appendChild(panel);

    var prevOverflow = document.body.style.overflow;
    var closed = false;
    /* Every exit route (button, backdrop, Esc) funnels through here, so an
       opts.onClose hook fires exactly once however the dialog was dismissed. */
    function close() {
      if (closed) return;
      closed = true;
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      document.body.style.overflow = prevOverflow;
      if (lastFocus && lastFocus.focus) lastFocus.focus();
      if (typeof opts.onClose === 'function') opts.onClose();
    }
    function onKey(e) {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      var f = panel.querySelectorAll('button:not(:disabled),input:not(:disabled),select,textarea,a[href]');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    closeBtn.addEventListener('click', close);
    overlay.addEventListener('mousedown', function (e) { if (e.target === overlay) close(); });
    document.addEventListener('keydown', onKey);

    document.body.style.overflow = 'hidden';
    document.body.appendChild(overlay);

    var handle = {
      body: body,
      close: close,
      done: function (heading, text, cta) {
        body.innerHTML = '<div class="sc-nsm__done"><div class="sc-nsm__done-tick">' + TICK + '</div>' +
          '<div class="sc-nsm__done-title"></div><p class="sc-nsm__done-sub"></p></div>';
        body.querySelector('.sc-nsm__done-title').textContent = heading;
        body.querySelector('.sc-nsm__done-sub').textContent = text;
        if (cta) {
          var a = document.createElement('a');
          a.className = 'sc-nsm__done-cta';
          a.href = cta.href;
          a.textContent = cta.label;
          body.querySelector('.sc-nsm__done').appendChild(a);
        }
        closeBtn.focus();
      }
    };

    if (typeof opts.render === 'function') opts.render(body, handle);
    var focusTarget = panel.querySelector('input,button.sc-nsm__choice,.sc-nsm__submit');
    if (focusTarget) focusTarget.focus();
    return handle;
  }

  /* Small helpers shared by the section scripts. */
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function field(labelText, input) {
    var wrap = el('div', 'sc-nsm__field');
    var lab = el('label', 'sc-nsm__label', labelText);
    var id = 'sc-nsm-' + Math.random().toString(36).slice(2, 9);
    input.id = id;
    lab.setAttribute('for', id);
    wrap.appendChild(lab);
    wrap.appendChild(input);
    return wrap;
  }
  function input(type, placeholder, value, autocomplete) {
    var i = el('input', 'sc-nsm__input');
    i.type = type;
    if (placeholder) i.placeholder = placeholder;
    if (value) i.value = value;
    if (autocomplete) i.autocomplete = autocomplete;
    return i;
  }
  function banner() {
    var b = el('div', 'sc-nsm__msg');
    b.hidden = true;
    b.setAttribute('role', 'status');
    /* The banner sits at the TOP of a scrollable dialog, while the button that
       triggers it sits at the bottom. Without this, a rejected booking looks
       like nothing happened at all. */
    function reveal() {
      try { b.scrollIntoView({ block: 'nearest' }); } catch (e) {}
    }
    return {
      node: b,
      error: function (t) { b.className = 'sc-nsm__msg sc-nsm__msg--error'; b.textContent = t; b.hidden = false; reveal(); },
      info:  function (t) { b.className = 'sc-nsm__msg sc-nsm__msg--info';  b.textContent = t; b.hidden = false; reveal(); },
      clear: function () { b.hidden = true; b.textContent = ''; }
    };
  }
  function choices(values, onPick, initial) {
    var wrap = el('div', 'sc-nsm__choices');
    var current = initial;
    values.forEach(function (v) {
      var b = el('button', 'sc-nsm__choice', v.label);
      b.type = 'button';
      b.setAttribute('aria-pressed', String(v.value === current));
      b.addEventListener('click', function () {
        current = v.value;
        wrap.querySelectorAll('.sc-nsm__choice').forEach(function (c) { c.setAttribute('aria-pressed', 'false'); });
        b.setAttribute('aria-pressed', 'true');
        onPick(v.value);
      });
      wrap.appendChild(b);
    });
    return wrap;
  }

  /* Shape check only. The backend re-validates, and Shopify's checkout is the
     real authority on whether an address can receive mail. */
  function isEmail(v) { return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v || '').trim()); }

  /* Same one phone rule as the backend (Code.gs normPhone_), kept in sync by
     hand: 10-digit Indian mobile -> E.164, or a foreign number that already
     carries its own +, else ''. */
  function normPhone(raw) {
    var s = String(raw || '').trim();
    var hasPlus = s.charAt(0) === '+';
    var d = s.replace(/[^\d]/g, '');
    if (!d) return '';
    var nat;
    if (hasPlus) {
      if (d.slice(0, 2) === '91') {
        nat = d.slice(2);
        return /^[6-9]\d{9}$/.test(nat) ? '+91' + nat : '';
      }
      return (d.length >= 8 && d.length <= 15) ? '+' + d : '';
    }
    d = d.replace(/^0+/, '');
    if (d.length === 12 && d.slice(0, 2) === '91') nat = d.slice(2);
    else if (d.length === 10) nat = d;
    else return '';
    return /^[6-9]\d{9}$/.test(nat) ? '+91' + nat : '';
  }
  function isPhone(v) { return !!normPhone(v); }

  /* yyyy-mm-dd for "today + n days" in the shop's timezone (IST).
     en-CA formats as yyyy-mm-dd. */
  function istDate(offsetDays) {
    var d = new Date(Date.now() + (offsetDays || 0) * 86400000);
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(d);
  }

  /* Times are STORED and sent to the backend as 24-hour "HH:MM"; these only
     change how they are SHOWN. 12-hour with AM/PM. "08:00" -> "8:00 AM",
     "13:30" -> "1:30 PM", "00:00" -> "12:00 AM", "12:00" -> "12:00 PM". */
  function to12h_(hhmm) {
    var p = String(hhmm || '').split(':');
    var h = parseInt(p[0], 10); if (isNaN(h)) return String(hhmm || '');
    var m = (p[1] || '00');
    var mer = h < 12 ? 'AM' : 'PM';
    var h12 = h % 12; if (h12 === 0) h12 = 12;
    return { h: h12, m: m, mer: mer, full: h12 + ':' + m + ' ' + mer };
  }
  function fmtTime(hhmm) { var t = to12h_(hhmm); return t.full || String(hhmm || ''); }
  /* A slot range. Collapse the AM/PM to one side when both ends share it:
     "8:00–8:30 AM"; keep both when it straddles noon/midnight. */
  function fmtRange(start, end) {
    var s = to12h_(start), e = to12h_(end);
    if (!s.mer || !e.mer) return fmtTime(start) + '–' + fmtTime(end);
    return s.mer === e.mer ? (s.h + ':' + s.m + '–' + e.h + ':' + e.m + ' ' + s.mer)
                           : (s.full + '–' + e.full);
  }

  window.SCNS = {
    cfg: CFG,
    enabled: !!CFG.endpoint,
    signedIn: !!CFG.identity,
    call: call,
    message: message,
    errText: errText,
    modal: modal,
    el: el,
    field: field,
    input: input,
    banner: banner,
    choices: choices,
    isEmail: isEmail,
    normPhone: normPhone,
    isPhone: isPhone,
    istDate: istDate,
    fmtTime: fmtTime,
    fmtRange: fmtRange
  };
}());
